import React, { useState, useMemo, useCallback } from "react";
import { BookOpen, Dumbbell, RotateCcw, Layers, Zap, ChevronsDownUp, ChevronsUpDown, FileText } from "lucide-react";
import { SequenceRow } from "./SequenceRow";
import { ModuleSection } from "./ModuleSection";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { CourseNodeWithStatus, CourseNodeType } from "@/types/courses";

interface CourseSequenceViewProps {
  nodes: CourseNodeWithStatus[];
  courseName: string;
  contentStepCounts: Map<string, number>;
  onNodeAction: (nodeId: string) => void;
}

type FilterType = "all" | CourseNodeType;

const FILTERS: {value: FilterType;label: string;}[] = [
{ value: "all", label: "All" },
{ value: "lesson", label: "Lessons" },
{ value: "practice", label: "Practice" },
{ value: "review", label: "Review" }];


interface ModuleGroup {
  title: string;
  moduleIndex: number;
  nodes: CourseNodeWithStatus[];
}

function BreakdownStat({ icon: Icon, label, value }: {icon: React.ElementType;label: string;value: number;}) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <Icon className="h-4 w-4 text-muted-foreground" />
      <span className="font-medium tabular-nums">{value}</span>
      <span className="text-muted-foreground">{label}</span>
    </div>);

}

export const CourseSequenceView: React.FC<CourseSequenceViewProps> = ({
  nodes,
  courseName,
  contentStepCounts,
  onNodeAction
}) => {
  const [filter, setFilter] = useState<FilterType>("all");
  const [openModuleIds, setOpenModuleIds] = useState<Set<string>>(new Set());
  const [openRowIds, setOpenRowIds] = useState<Set<string>>(new Set());

  const sorted = useMemo(
    () => [...nodes].sort((a, b) => a.orderIndex - b.orderIndex),
    [nodes]
  );

  const filtered = useMemo(
    () => filter === "all" ? sorted : sorted.filter((n) => n.type === filter),
    [sorted, filter]
  );

  // Determine if modules exist
  const hasModules = useMemo(
    () => sorted.some((n) => n.moduleTitle != null),
    [sorted]
  );

  // Group filtered nodes into modules
  const moduleGroups = useMemo<ModuleGroup[]>(() => {
    if (!hasModules) return [];

    const groupMap = new Map<string, ModuleGroup>();
    for (const node of filtered) {
      const key = node.moduleTitle ?? "__ungrouped__";
      if (!groupMap.has(key)) {
        groupMap.set(key, {
          title: node.moduleTitle ?? "Other",
          moduleIndex: node.moduleIndex,
          nodes: []
        });
      }
      groupMap.get(key)!.nodes.push(node);
    }

    return Array.from(groupMap.values()).sort((a, b) => a.moduleIndex - b.moduleIndex);
  }, [filtered, hasModules]);

  // Breakdown stats
  const stats = useMemo(() => {
    const lessons = nodes.filter((n) => n.type === "lesson").length;
    const practice = nodes.filter((n) => n.type === "practice").length;
    const review = nodes.filter((n) => n.type === "review").length;
    const totalSteps = nodes.reduce((s, n) => s + (contentStepCounts.get(n.id) ?? 0), 0);
    const totalXp = nodes.reduce((s, n) => s + n.xpReward, 0);
    return { lessons, practice, review, totalSteps, totalXp };
  }, [nodes, contentStepCounts]);

  const toggleRow = useCallback((id: string) => {
    setOpenRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else
      next.add(id);
      return next;
    });
  }, []);

  const toggleModule = useCallback((title: string) => {
    setOpenModuleIds((prev) => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);else
      next.add(title);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    if (hasModules) {
      setOpenModuleIds(new Set(moduleGroups.map((g) => g.title)));
    }
    setOpenRowIds(new Set(filtered.map((n) => n.id)));
  }, [filtered, hasModules, moduleGroups]);

  const collapseAll = useCallback(() => {
    setOpenModuleIds(new Set());
    setOpenRowIds(new Set());
  }, []);

  return (
    <div className="space-y-4">
      {/* Breakdown summary */}
      <Card className="border-border/60">
        <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-2 px-5 py-3.5">
          <BreakdownStat icon={BookOpen} label="Lessons" value={stats.lessons} />
          <BreakdownStat icon={Dumbbell} label="Practice" value={stats.practice} />
          <BreakdownStat icon={RotateCcw} label="Review" value={stats.review} />
          <BreakdownStat icon={Layers} label="Steps" value={stats.totalSteps} />
          <BreakdownStat icon={Zap} label="XP" value={stats.totalXp} />
        </CardContent>
      </Card>

      {/* Filter toolbar */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-0 rounded-lg border border-border bg-background p-0.5">
          {FILTERS.map((f) =>
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={cn("font-medium px-3 py-1 rounded-md transition-colors text-sm",

            filter === f.value ?
            "text-foreground bg-muted" :
            "text-muted-foreground hover:text-foreground"
            )}>

              {f.label}
            </button>
          )}
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={expandAll}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            title="Expand all">

            <ChevronsUpDown className="h-4 w-4" />
          </button>
          <button
            onClick={collapseAll}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            title="Collapse all">

            <ChevronsDownUp className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Syllabus list */}
      <div className="rounded-2xl border border-border bg-card divide-y divide-border overflow-hidden py-0">
        {hasModules ?
        <>
            {moduleGroups.map((group) => {
            // Calculate index offset: sum of nodes in previous groups
            const offset = moduleGroups.
            slice(0, moduleGroups.indexOf(group)).
            reduce((s, g) => s + g.nodes.length, 0);

            return (
              <ModuleSection
                key={group.title}
                title={group.title}
                moduleIndex={group.moduleIndex}
                nodes={group.nodes}
                contentStepCounts={contentStepCounts}
                isModuleOpen={openModuleIds.has(group.title)}
                onToggleModule={() => toggleModule(group.title)}
                openRowIds={openRowIds}
                onToggleRow={toggleRow}
                onNodeAction={onNodeAction}
                indexOffset={offset} />);


          })}
          </> :

        filtered.map((node, i) =>
        <SequenceRow
          key={node.id}
          node={node}
          index={i}
          stepCount={contentStepCounts.get(node.id) ?? 0}
          isOpen={openRowIds.has(node.id)}
          onToggle={() => toggleRow(node.id)}
          onNodeAction={onNodeAction} />

        )
        }
        {filtered.length === 0 &&
        <div className="flex flex-col items-center gap-2 px-4 py-12">
            <FileText className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">
              {filter === "all" ? "No content in this course yet." : `No ${filter} nodes found.`}
            </p>
          </div>
        }
      </div>
    </div>);

};